import styled from "@emotion/styled"
import { Link } from "utils"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

const desktop = `@media (min-width: ${breakpoints.lg}px)`

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: ${breakpoints.xl}px;
  margin: 0 auto;
  padding: ${sizes.spacing[24]}px ${sizes.spacing[16]}px ${sizes.spacing[64]}px;
  gap: ${sizes.spacing[40]}px;

  ${desktop} {
    padding: ${sizes.spacing[40]}px ${sizes.spacing[80]}px
      ${sizes.spacing[96]}px;
    gap: ${sizes.spacing[48]}px;
  }
`

export const Hero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${sizes.spacing[24]}px;
  width: 100%;

  ${desktop} {
    gap: ${sizes.spacing[40]}px;
  }
`

export const Logo = styled.div`
  /* No logo on mobile per the design — it only shows from desktop up
     (EGDE-426). */
  display: none;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }

  ${desktop} {
    display: block;
    width: 88px;
    height: 88px;
  }
`

export const HeadingRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${sizes.spacing[12]}px;
  width: 100%;
`

export const HeadingChevronWrap = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 13px;
  height: 22px;

  svg {
    width: 100%;
    height: 100%;
  }

  ${desktop} {
    width: 19px;
    height: 32px;
  }
`

export const Heading = styled.h2`
  margin: 0;
  font-size: 40px;
  font-weight: 700;
  line-height: 44px;
  letter-spacing: 0;
  color: ${colors.primitives.neutral600};
  text-align: center;

  ${desktop} {
    font-size: 56px;
    line-height: 64px;
  }
`

// ---------------------------------------------------------------------------
// Search field + autocomplete
// ---------------------------------------------------------------------------

export const FieldWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 675px;
`

export const Field = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
  height: 58px;
  padding: 0 ${sizes.spacing[16]}px;
  border: 1px solid ${colors.primitives.neutral300};
  border-radius: ${sizes.borderRadius.full}px;
  background: ${colors.primitives.neutral0};
  transition: border-color 0.15s ease;

  &:focus-within {
    border-color: ${colors.primitives.primary500};
  }
`

export const FieldIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${colors.primitives.neutral600};

  svg {
    width: 20px;
    height: 20px;
  }
`

export const FieldButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: ${sizes.spacing[4]}px;
  border: none;
  background: transparent;
  color: ${colors.primitives.neutral600};
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    opacity: 0.7;
  }
`

export const Input = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  /* 18px including the placeholder at every breakpoint — mobile was 16px
     (EGDE-427). */
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
  color: ${colors.primitives.neutral600};
  text-align: start;

  &::placeholder {
    color: ${colors.primitives.neutral400};
  }

  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }
`

export const Suggestions = styled.ul`
  position: absolute;
  inset-inline: 0;
  top: calc(100% + ${sizes.spacing[8]}px);
  z-index: 10;
  margin: 0;
  padding: ${sizes.spacing[8]}px 0;
  list-style: none;
  background: ${colors.primitives.neutral0};
  border: 1px solid ${colors.primitives.neutral300};
  border-radius: ${sizes.borderRadius[16]}px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  max-height: 320px;
  overflow-y: auto;
`

export const SuggestionItem = styled.li`
  & > button {
    display: block;
    width: 100%;
    padding: ${sizes.spacing[12]}px ${sizes.spacing[16]}px;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: 16px;
    line-height: 24px;
    color: ${colors.primitives.neutral600};
    text-align: start;
    cursor: pointer;

    &:hover,
    &[data-active="true"] {
      background: ${colors.primitives.neutral100};
    }
  }
`

// ---------------------------------------------------------------------------
// Recommended searches
// ---------------------------------------------------------------------------

export const RecommendedSection = styled.div`
  display: flex;
  flex-direction: column;
  /* Start-aligned (right in RTL) on mobile, centered from desktop up
     (EGDE-428). */
  align-items: flex-start;
  gap: ${sizes.spacing[24]}px;
  width: 100%;

  ${desktop} {
    align-items: center;
  }
`

export const RecommendedHeading = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
  color: ${colors.primitives.neutral600};
  text-align: start;

  ${desktop} {
    font-size: 20px;
    line-height: 28px;
    text-align: center;
  }
`

export const ChipRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: ${sizes.spacing[12]}px;
  max-width: 980px;

  ${desktop} {
    justify-content: center;
  }
`

export const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  padding: 0 ${sizes.spacing[24]}px;
  border: 1px solid ${colors.primitives.neutral300};
  border-radius: ${sizes.borderRadius.full}px;
  background: ${colors.primitives.neutral0};
  font-family: inherit;
  /* Body 1 (20px) at every breakpoint per the design (EGDE-425 desktop was
     18px, EGDE-429 mobile was 16px). */
  font-size: 20px;
  font-weight: 500;
  line-height: 1;
  color: ${colors.primitives.neutral600};
  white-space: nowrap;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover {
    border-color: ${colors.primitives.primary500};
    background: ${colors.primitives.neutral100};
  }
`

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export const ResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[24]}px;
  width: 100%;
  max-width: 1200px;
`

export const CategoryRow = styled.div`
  display: flex;
  flex-direction: row;
  /* EGDE-461: one scrollable row like the magazine-lobby tabs — wrapping to a
     second line on mobile was rejected by QA. The scrollbar stays hidden so it
     reads as a swipeable carousel. */
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  gap: ${sizes.spacing[24]}px;
  /* Tabs share a thin baseline; the active tab paints an orange segment on it
     (matches the magazine lobby tabs) rather than pill chips (EGDE-431). */
  border-bottom: 1px solid ${colors.primitives.neutral300};
`

export const CategoryChip = styled.button<{ $active?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${sizes.spacing[4]}px ${sizes.spacing[12]}px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 16px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  line-height: 1;
  color: ${({ $active }) =>
    $active ? colors.primitives.neutral600 : colors.primitives.neutral500};
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.15s ease;

  &::after {
    content: "";
    position: absolute;
    inset-inline: 0;
    bottom: -1px;
    height: 2px;
    background: ${({ $active }) =>
      $active ? colors.primitives.primary500 : "transparent"};
  }

  &:hover {
    color: ${colors.primitives.neutral600};
  }
`

export const CountText = styled.div`
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
  color: ${colors.primitives.neutral600};
  text-align: start;

  /* The result count reads as a regular-weight sentence — the number is not
     emphasised (EGDE-430). */
  b {
    font-weight: 500;
  }

  ${desktop} {
    font-size: 20px;
    line-height: 28px;
  }
`

export const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${colors.primitives.neutral300};
`

export const ResultRow = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;
  padding: ${sizes.spacing[24]}px 0;
  border-bottom: 1px solid ${colors.primitives.neutral300};
  text-decoration: none;
  color: inherit;
  width: 100%;
  text-align: start;

  &:hover h3 {
    color: ${colors.primitives.primary500};
  }
`

export const ResultTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
  color: ${colors.primitives.neutral600};
  text-align: start;
  transition: color 0.15s ease;

  ${desktop} {
    font-size: 20px;
    line-height: 28px;
  }
`

export const ResultSummary = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${colors.primitives.neutral600};
  text-align: start;
  max-width: 1000px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${desktop} {
    font-size: 18px;
    line-height: 26px;
  }
`

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export const Pagination = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${sizes.spacing[16]}px;
  margin-top: ${sizes.spacing[8]}px;
`

export const PageButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${sizes.borderRadius.full}px;
  border: 1px solid
    ${({ $active }) =>
      $active ? colors.primitives.warning500 : colors.primitives.neutral300};
  background: ${({ $active }) =>
    $active ? colors.primitives.secondary500 : colors.primitives.neutral0};
  font-family: inherit;
  /* Active page keeps the same text size as the rest — it stands out via the
     filled circle + weight, not a larger number (EGDE-413). */
  font-size: 16px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  color: ${({ $active }) =>
    $active ? colors.primitives.neutral600 : colors.primitives.neutral500};
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover:not([disabled]) {
    border-color: ${colors.primitives.warning500};
  }
`

export const ArrowButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: ${colors.primitives.neutral500};
  cursor: pointer;

  svg {
    width: 8px;
    height: 14px;
  }

  &:hover:not([disabled]) {
    color: ${colors.primitives.neutral600};
  }

  &[disabled] {
    opacity: 0.3;
    cursor: default;
  }

  /* "next" advances to higher pages — in RTL it sits on the left and points left;
     the prev arrow is mirrored via this modifier. */
  &[data-dir="prev"] svg {
    transform: scaleX(-1);
  }
`

// ---------------------------------------------------------------------------
// No results
// ---------------------------------------------------------------------------

export const NoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${sizes.spacing[24]}px;
  padding: ${sizes.spacing[40]}px 0;
  text-align: center;
  width: 100%;
`

export const NoResultsIcon = styled.div`
  width: 72px;
  height: 72px;
  color: ${colors.primitives.neutral600};

  svg {
    width: 100%;
    height: 100%;
  }

  ${desktop} {
    width: 88px;
    height: 88px;
  }
`

export const NoResultsTitle = styled.div`
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
  color: ${colors.primitives.neutral600};

  b {
    font-weight: 700;
  }

  ${desktop} {
    font-size: 22px;
    line-height: 30px;
  }
`

export const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  padding: 0 ${sizes.spacing[40]}px;
  border: none;
  border-radius: ${sizes.borderRadius.full}px;
  background: ${colors.gradients.gradientCta};
  font-family: inherit;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  color: ${colors.primitives.neutral600};
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }

  ${desktop} {
    height: 58px;
  }
`

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

export const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${sizes.spacing[48]}px;
  width: 100%;
`

export const Spinner = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid ${colors.primitives.neutral200};
  border-top-color: ${colors.primitives.primary500};
  animation: searchspin 0.8s linear infinite;

  @keyframes searchspin {
    to {
      transform: rotate(360deg);
    }
  }
`

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`
